import { useState } from "react";
import { FaEye, FaEyeSlash, FaCamera } from "react-icons/fa";
import "./SignUp.css";
import {
  NavLink,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { Helmet } from "react-helmet-async";

const SignUp = ({ dark, setUser, MyBaseUrl }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    password_confirmation: "",
    image: null,
  });

  const helmetData = (
    <Helmet>
      <title>Sign Up | Create Your FadaSports Account</title>
      <meta name="description" content="Create your FadaSports account to follow matches, manage favorites, and personalize your sports experience." />
      <meta name="keywords" content="sign up, create account, registration, join FadaSports" />
      <meta property="og:title" content="Sign Up for FadaSports" />
      <meta property="og:description" content="Create your free FadaSports account." />
      <meta property="og:image" content="https://fadasports.com/static/media/mobilelogo.d0257d49e02d3094cce0.png" />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
    </Helmet>
  );

  const [submitting, setSubmitting] =
    useState(false);

  const [errorMsg, setErrorMsg] =
    useState("");

  const [showPassword, setShowPassword] =
    useState(false);

  const [
    showConfirmPassword,
    setShowConfirmPassword,
  ] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]:
        e.target.name === "image"
          ? e.target.files[0]
          : e.target.value,
    });
  };

  const setCookie = (
    name,
    value,
    days = 50
  ) => {
    const expires = new Date(
      Date.now() +
        days *
          24 *
          60 *
          60 *
          1000
    ).toUTCString();

    document.cookie = `${name}=${encodeURIComponent(
      value
    )}; expires=${expires}; path=/`;
  };

  const validateForm = () => {
    if (!formData.username.trim()) {
      setErrorMsg("Username required");
      return false;
    }

    if (!formData.email.trim()) {
      setErrorMsg("Email required");
      return false;
    }

    const emailRegex =
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(formData.email)) {
      setErrorMsg("Invalid email");
      return false;
    }

    if (formData.password.length < 6) {
      setErrorMsg(
        "Password minimum 6 characters"
      );
      return false;
    }

    if (
      formData.password !==
      formData.password_confirmation
    ) {
      setErrorMsg(
        "Passwords do not match"
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setErrorMsg("");

    if (!validateForm()) return;

    try {
      setSubmitting(true);

      const response = await fetch(
        `${MyBaseUrl}/auth/register`,
        {
          method: "POST",
          headers: {
            "Content-Type":
              "application/json",
            Accept:
              "application/json",
          },
          body: JSON.stringify({
            name: formData.username,
            email: formData.email,
            password:
              formData.password,
            password_confirmation:
              formData.password_confirmation,
          }),
        }
      );

      const data =
        await response.json();

      if (
        !response.ok ||
        data.status !== "success"
      ) {
        throw new Error(
          data.message ||
            "Registration failed"
        );
      }

      const token =
        data?.data?.token;

      const registeredUser =
        data?.data?.user || {
          name: formData.username,
          email: formData.email,
        };

      if (token) {
        localStorage.setItem(
          "authToken",
          token
        );

        localStorage.setItem(
          "authUser",
          JSON.stringify(
            registeredUser
          )
        );

        setCookie(
          "authToken",
          token
        );
      }

      setUser({
        ...registeredUser,
        token: token
      });

      const redirectTo =
        location.state?.from
          ?.pathname || "/";

      navigate(redirectTo, {
        replace: true,
      });
    } catch (error) {
      setErrorMsg(
        error.message ||
          "Something went wrong"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (

    <>

    <Helmet>
  <title>Sign Up | Create Your FadaSports Account</title>

  <meta
    name="description"
    content="Create your FadaSports account to follow matches, manage favorites, and personalize your sports experience."
  />

  <meta
    name="keywords"
    content="sign up, register, football account, create account, join FadaSports"
  />

  <meta property="og:title" content="Sign Up | FadaSports" />
  <meta
    property="og:description"
    content="Create your free FadaSports account to follow live football."
  />
  <meta property="og:type" content="website" />

  <meta property="og:image" content="https://fadasports.com/static/media/mobilelogo.d0257d49e02d3094cce0.png" />

  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="Sign Up | FadaSports" />
  <meta
    name="twitter:description"
    content="Register to access FadaSports football updates and features."
  />
  <meta name="twitter:image" content="https://fadasports.com/static/media/mobilelogo.d0257d49e02d3094cce0.png" />

  <meta name="robots" content="noindex, follow" />
</Helmet>

      <div className="homer">
        <section className="signup-container">
          <form
            onSubmit={handleSubmit}
            className="signup-card"
            style={
              dark
                ? {
                    background:
                      "#111111e7",
                  }
                : {}
            }
          >
            <h1 style={dark ? {} : { color: "#161819" }}>
              Join the Community
            </h1>

            {errorMsg && (
              <p
                style={{
                  color:
                    "#ff4d4f",
                }}
              >
                {errorMsg}
              </p>
            )}

            <input
              type="text"
              name="username"
              placeholder="Username"
              value={
                formData.username
              }
              onChange={
                handleChange
              }
            />

            <input
              type="email"
              name="email"
              placeholder="Email"
              value={
                formData.email
              }
              onChange={
                handleChange
              }
            />

            <div style={{ position: "relative", width: "100%" }} className="inn">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Password"
                value={formData.password}
                onChange={handleChange}
                style={{
                  width: "100%",
                  paddingRight: "45px",
                }}
              />

              <span
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: "absolute",
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                  color: "#888",
                  display: "flex",
                  alignItems: "center",
                    scale: "0.8"
                }}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>

            <div style={{ position: "relative", width: "100%" }} className="inn">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="password_confirmation"
                  placeholder="Confirm Password"
                  value={formData.password_confirmation}
                  onChange={handleChange}
                  style={{
                    width: "100%",
                    paddingRight: "45px",
                  }}
                />

                <span
                  onClick={() =>
                    setShowConfirmPassword(!showConfirmPassword)
                  }
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    color: "#888",
                    display: "flex",
                    alignItems: "center",
                    scale: "0.8"
                  }}
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>

            <div className="upload">
              <label className="upload-box">

                {formData.image ? (
                  <div className="upload-preview-wrapper">
                    <img
                      src={URL.createObjectURL(formData.image)}
                      alt="preview"
                      className="upload-preview"
                    />

                    <div className="upload-overlay">
                      <FaCamera className="camera-icon" />
                      <p>Change Photo</p>
                    </div>
                  </div>
                ) : (
                  <div className="upload-placeholder">
                    <FaCamera className="camera-icon" />
                    <p>Upload Profile Photo</p>
                  </div>
                )}

                <input
                  type="file"
                  name="photo"
                  accept="image/*"
                  onChange={handleChange}
                  hidden
                />
              </label>
            </div>
            <button
              type="submit"
              disabled={
                submitting
              }
            >
              {submitting
                ? "Creating Account..."
                : "Sign Up"}
            </button>

            <p style={dark ? {} : {color: "#3e3f3f"}}>
              Already have an
              account?{" "}
              <NavLink to="/login">
                Login
              </NavLink>
            </p>
          </form>
        </section>

        
       
      </div>
    </>
  );
};

export default SignUp;